import { Component, type ErrorInfo, type ReactNode } from 'react'

type FallbackProps = {
  error: Error
  resetError: () => void
}

type ErrorBoundaryProps = {
  children: ReactNode
  fallback: (props: FallbackProps) => ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

type ErrorBoundaryState = {
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  // Arrow property ensures `this` is always bound when passed as a callback
  resetError = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    if (this.state.error) {
      return this.props.fallback({ error: this.state.error, resetError: this.resetError })
    }
    return this.props.children
  }
}
